import React from "react";
import { FileText, Clock, CheckCircle, AlertTriangle, Headset, ExternalLink, ChevronRight } from "lucide-react";
import { useApp } from "../store";

export default function Warranty() {
  const { t } = useApp();
  const w = t.warranty;

  return (
    <main className="pt-32 pb-32 bg-white min-h-screen" data-testid="warranty-page">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-16 text-center">
          <div className="overline mb-4">{w.subtitle}</div>
          <h1 className="font-serif-display text-5xl md:text-7xl text-slate-900 mb-6">{w.h1}</h1>
          <p className="text-slate-600 max-w-3xl mx-auto text-lg leading-relaxed">{w.summary}</p>
        </div>

        {/* Claim Path CTA */}
        <section className="mb-20 bg-slate-50 border border-slate-200 rounded-lg p-8 text-center">
          <h2 className="font-serif-display text-3xl text-slate-900 mb-4">{w.claimPathTitle}</h2>
          <p className="text-slate-600 mb-8 max-w-2xl mx-auto">
            {w.claimPathBody}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/warranty/request" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
              <ExternalLink size={16} /> {w.cta1}
            </a>
            <a href="/faq" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
              <ExternalLink size={16} /> {w.cta2}
            </a>
            <button onClick={() => {
              const el = document.querySelector("[data-testid='floating-chat-btn']");
              if (el) el.click();
            }} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
              <Headset size={16} /> {w.cta3}
            </button>
          </div>
          <p className="text-sm text-slate-500 mt-6">
            * {w.legalNote}
          </p>
        </section>

        {/* How it works */}
        <section className="mb-20">
          <h2 className="font-serif-display text-3xl md:text-4xl text-slate-900 mb-8">{w.howItWorksTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {w.howItWorks.map((item, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-lg p-6">
                <div className="w-10 h-10 rounded-full bg-sky-500 text-white flex items-center justify-center mb-4 font-bold">{i+1}</div>
                <h3 className="font-serif-display text-xl text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Coverage */}
        <section className="mb-20">
          <h2 className="font-serif-display text-3xl md:text-4xl text-slate-900 mb-8">{w.coverageTitle}</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50">
                  <th className="border border-slate-200 px-6 py-4 text-left text-slate-900 font-semibold">{w.tableHeaderComponent}</th>
                  <th className="border border-slate-200 px-6 py-4 text-left text-slate-900 font-semibold">{w.tableHeaderPeriod}</th>
                </tr>
              </thead>
              <tbody>
                {w.coverage.map((item, i) => (
                  <tr key={i} className={i%2===0 ? "bg-white" : "bg-slate-50"}>
                    <td className="border border-slate-200 px-6 py-4 text-slate-700">{item.component}</td>
                    <td className="border border-slate-200 px-6 py-4 text-slate-700 font-medium">{item.period}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-slate-500 mt-4">* {w.legalNote}</p>
        </section>

        {/* Exclusions */}
        <section className="mb-20">
          <h2 className="font-serif-display text-3xl md:text-4xl text-slate-900 mb-8 flex items-center gap-3">
            <AlertTriangle className="text-amber-500" /> {w.exclusionsTitle}
          </h2>
          <ul className="space-y-3">
            {w.exclusions.map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-slate-700">
                <span className="mt-1 text-amber-500">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Required Documents */}
        <section className="mb-20 bg-slate-50 border border-slate-200 rounded-lg p-8">
          <h2 className="font-serif-display text-3xl md:text-4xl text-slate-900 mb-8 flex items-center gap-3">
            <FileText className="text-sky-500" /> {w.documentsTitle}
          </h2>
          <p className="text-slate-600 mb-6">{w.documentsIntro}</p>
          <ul className="space-y-4">
            {w.documents.map((item, i) => (
              <li key={i} className="flex items-start gap-4 p-4 bg-white rounded-lg border border-slate-200">
                <CheckCircle className="text-emerald-500 mt-1 flex-shrink-0" size={20} />
                <span className="text-slate-700">{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Claim Process */}
        <section className="mb-20">
          <h2 className="font-serif-display text-3xl md:text-4xl text-slate-900 mb-8">{w.claimProcessTitle}</h2>
          <div className="space-y-6">
            {w.claimProcess.map((step, i) => (
              <div key={i} className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="w-8 h-8 rounded-full bg-sky-500 text-white flex items-center justify-center flex-shrink-0 font-bold text-sm">{i+1}</div>
                <p className="text-slate-700 leading-relaxed">{step}</p>
              </div>
            ))}
          </div>
        </section>

        {/* SLA */}
        <section className="mb-20 bg-slate-50 border border-slate-200 rounded-lg p-8">
          <h2 className="font-serif-display text-3xl md:text-4xl text-slate-900 mb-4 flex items-center gap-3">
            <Clock className="text-sky-500" /> {w.slaTitle}
          </h2>
          <p className="text-slate-700 leading-relaxed">{w.sla}</p>
          <p className="text-sm text-slate-500 mt-4">* {w.legalNote}</p>
        </section>

        {/* Internal links */}
        <section className="mb-20 text-center">
          <p className="text-slate-600 mb-6">{w.relatedPages}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/faq" className="text-sky-500 hover:underline inline-flex items-center gap-1">
              FAQ <ChevronRight size={14} />
            </a>
            <a href="/support" className="text-sky-500 hover:underline inline-flex items-center gap-1">
              {w.cta3} <ChevronRight size={14} />
            </a>
            <a href="/compare" className="text-sky-500 hover:underline inline-flex items-center gap-1">
              Compare models <ChevronRight size={14} />
            </a>
          </div>
        </section>

        {/* Legal Notice */}
        <section className="border-t border-slate-200 pt-8">
          <p className="text-slate-500 text-sm leading-relaxed">{w.legal}</p>
        </section>
      </div>
    </main>
  );
}
